import axios from "axios";
import { loadRistaMenuFromAPI } from "./ristaAdapter";
import { cacheBranches, getCachedBranches } from "./indexedDBUtils";
import { API_BASE_URL as GOTTEA_API_BASE_URL } from "./config/payments";
import { filterBlockedSkusFromMenu } from "./utils/menuSkuPolicy";

const BLUE_TOKAI_API_BASE_URL = "https://api.bluetokaicoffee.com/api";
const RISTA_API_BASE_URL = "https://rdx8azn9lh.execute-api.ap-south-1.amazonaws.com/default/rista-tab-menu-api";

// Flag to switch between Blue Tokai API and Rista API
const USE_RISTA_API = true;
const USE_GOTTEA_ORDERING_API = true;

// Fetch cafe list from gottea-api. The backend normalizes branch fields and
// controls ordering availability / Razorpay account selection.
export const fetchCafeList = async () => {
  try {
    console.log('Fetching cafe list from backend...');

    const response = await fetch(`${GOTTEA_API_BASE_URL}/config/menu/branches`);
    const payload = await response.json().catch(() => ({}));

    if (!response.ok || !payload?.success) {
      throw new Error(payload?.error || `Unable to fetch cafes (${response.status})`);
    }

    const transformedBranches = Array.isArray(payload.data) ? payload.data : [];

    console.log('Backend branches loaded:', transformedBranches.length);

    // Cache the branches
    await cacheBranches(transformedBranches);

    return transformedBranches;
  } catch (error) {
    console.error("Error fetching cafe list from backend:", error);

    // Use cached branch data only as an offline fallback. Ordering enable/disable
    // must come from the backend whenever the backend is reachable.
    const cachedBranches = await getCachedBranches();
    if (cachedBranches && cachedBranches.length > 0) {
      console.log('Backend failed, using cached branches as fallback');
      return cachedBranches;
    }

    throw error;
  }
};

// Updated function to fetch menu list - now accepts cafeCode
export const fetchMenuList = async (cafeCode = "245") => {
  if (USE_GOTTEA_ORDERING_API) {
    try {
      const params = new URLSearchParams({
        branchCode: cafeCode,
        fulfillmentType: "dine_in",
      });
      const response = await fetch(`${GOTTEA_API_BASE_URL}/ordering/menu?${params.toString()}`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || `Ordering menu failed (${response.status})`);
      }
      const data = payload.data || {};
      return filterBlockedSkusFromMenu({
        categories: Array.isArray(data.categories) ? data.categories : [],
        tags: Array.isArray(data.tags) ? data.tags : [],
        productNames: Array.isArray(data.productNames) ? data.productNames : [],
        taxTypes: Array.isArray(data.taxTypes) ? data.taxTypes : [],
        store: data.branch || null,
      });
    } catch (error) {
      console.warn("Ordering API menu unavailable, falling back to Lambda:", error);
    }
  }

  // Use Rista API (dynamic API call) if flag is enabled
  if (USE_RISTA_API) {
    try {
      return filterBlockedSkusFromMenu(
        await loadRistaMenuFromAPI(RISTA_API_BASE_URL, cafeCode)
      );
    } catch (error) {
      console.error("Error loading Rista menu:", error);
      return { categories: [], tags: [], productNames: [], store: null };
    }
  }

  // Original Blue Tokai API implementation
  try {
    const response = await axios.post(
      `${BLUE_TOKAI_API_BASE_URL}/tab-menu-list`,
      { cafeCode: cafeCode }, // Use dynamic cafeCode based on selected cafe
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    const categories = response.data?.data?.appCategories || [];
    const allTags = response.data?.data?.allTags || [];
    const store = response.data?.data?.store || null;
    const productNames = [];

    // Format tags
    const formattedTags = allTags.map((tag) => ({
      id: tag.id,
      name: tag.name,
      description: "",
      categoryImage: tag.icon?.fullPath || "",
      products: [],
      isTag: true,
    }));

    // Format categories (parent only for menu, but include subcategory info in products)
    const formattedCategories = categories.map((cat) => {
      const allProducts = [];
      let subCategories = [];
      // Products from subcategories
      (cat.data || []).forEach((subCat) => {
        subCategories.push({
          name: subCat.categoryName,
          id: subCat.id
        });
        const subCatProducts = (subCat.products || []).map((p) => {
          productNames.push(p.title.toLowerCase());
          return ({
          id: p.id,
          skuCode: p.skuCode || p.sku || "",
          name: p.title,
          price: p.price || 0,
          taxTypeIds: p.taxTypeIds || [],
          isPriceIncludesTax: Boolean(p.isPriceIncludesTax),
          imageUrl: p.image?.fullPath || p.productDefaultImage || "",
          description: p.description || "",
          nutritions: p.nutritions || [],
          variations: p.variations || [],
          isVeg: p.isVeg,
          tags: (p.tags || []),
          subCategoryName: subCat.categoryName, // subcategory info
        })
      });
        allProducts.push(...subCatProducts);
      });

      // Products directly under parent category
      const parentProducts = (cat.products || []).map((p) => ({
        id: p.id,
        skuCode: p.skuCode || p.sku || "",
        name: p.title,
        price: p.variations?.[0]?.price || p.price || 0,
        taxTypeIds: p.taxTypeIds || [],
        isPriceIncludesTax: Boolean(p.isPriceIncludesTax),
        imageUrl: p.image?.fullPath || p.productDefaultImage || "",
        description: p.description || "",
        nutritions: p.nutritions || [],
        variations: p.variations || [],
        isVeg: p.isVeg,
        tags: (p.tags || []),
        subCategoryName: p.subCategoryName || null,
      }));

      return {
        id: cat.id,
        subCategories,
        parentName: cat.categoryName, // only parent name
        description: cat.description || "",
        categoryImage: cat.categoryImage?.fullPath || "",
        products: [...parentProducts, ...allProducts],
      };
    });

    return filterBlockedSkusFromMenu({
      categories: formattedCategories,
      tags: formattedTags,
      productNames: productNames,
      store: store,
    });
  } catch (error) {
    console.error("Error fetching menu list:", error);
    return { categories: [], tags: [], productNames: [], store: null };
  }
};

// New function to fetch sold-out items
export const fetchSoldOutItems = async (branch = "CA-096", options = {}) => {
  const strict = Boolean(options?.strict);

  if (USE_GOTTEA_ORDERING_API) {
    try {
      const params = new URLSearchParams({
        branchCode: branch,
        fulfillmentType: "dine_in",
      });
      const response = await fetch(`${GOTTEA_API_BASE_URL}/ordering/menu?${params.toString()}`);
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload?.success) {
        throw new Error(payload?.error || `Ordering sold-out failed (${response.status})`);
      }
      return payload.data?.soldOutMap || {};
    } catch (error) {
      console.warn("Ordering API sold-out unavailable, falling back to Lambda:", error);
    }
  }

  try {
    const url = `${RISTA_API_BASE_URL}?branch=${branch}&requestType=soldout`;
    console.log(`Fetching sold-out items from: ${url}`);
    
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch sold-out items: ${response.status} ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log('Sold-out items response:', data);
    
    // Transform response to a map of skuCode -> item info for easy lookup
    // Response structure: { success: boolean, data: { data: [{ skuCode, shortName, longName }] } }
    if (data.success && data.data && data.data.data && Array.isArray(data.data.data)) {
      const soldOutMap = {};
      data.data.data.forEach(item => {
        soldOutMap[item.skuCode] = {
          shortName: item.shortName,
          longName: item.longName,
          skuCode: item.skuCode
        };
      });
      console.log('Sold-out items map:', soldOutMap);
      return soldOutMap;
    }

    if (strict) {
      throw new Error("Unexpected sold-out items response");
    }
    
    return {};
  } catch (error) {
    console.error("Error fetching sold-out items:", error);
    if (strict) {
      throw error;
    }
    return {}; // Return empty map on error, don't break the app
  }
};

const detectMediaType = (url = "", declaredType = "") => {
  const normalizedDeclared = declaredType.toLowerCase();

  if (normalizedDeclared.includes("video")) {
    return "video";
  }

  if (normalizedDeclared.includes("image")) {
    return "image";
  }

  const cleanedUrl = url.split("?")[0];
  const extension = cleanedUrl.split(".").pop()?.toLowerCase();

  if (["mp4", "webm", "ogg", "mov", "m4v"].includes(extension)) {
    return "video";
  }

  if (["jpg", "jpeg", "png", "gif", "bmp", "svg", "webp", "avif", "jfif"].includes(extension)) {
    return "image";
  }

  return "unknown";
};

// Fetch banners from Firebase Firestore
// 1. Query cafeTVs collection where storeCode = cafeCode
// 2. Get menuItems for each menuItemId found
// 3. Return banners with imageUrl from menuItems
export const fetchBanners = async ({
  cafeCode = "0"
} = {}) => {
  // Banners came from the Blue Tokai Firebase project; this app has no Firestore client.
  if (typeof firestore === 'undefined') return [];

  try {
    console.log('Fetching banners from Firebase for cafeCode:', cafeCode);

    // Query cafeTVs collection for this store
    const cafeTVsSnapshot = await firestore
      .collection('cafeTVs')
      .where('storeCode', '==', cafeCode)
      .get();

    if (cafeTVsSnapshot.empty) {
      console.log('No cafeTVs found for cafeCode:', cafeCode);
      return [];
    }

    // Get all cafeTVs data with screenPosition
    const cafeTVsData = cafeTVsSnapshot.docs.map(doc => doc.data());
    console.log('Found cafeTVs:', cafeTVsData.length);

    const buildSingleBanner = ({ cafeTV, menuItem, screenNumber, mediaUrl, mediaType, duration, idSuffix = "" }) => ({
      id: `${cafeTV.menuItemId}${idSuffix}`,
      duration: duration || menuItem.duration || 5,
      title: menuItem.title || "",
      category: null,
      subCategory: null,
      redirectMode: null,
      preference: screenNumber,
      option: null,
      displayOn: null,
      imageType: mediaType,
      isActive: true,
      mediaUrl,
      mediaType,
      image: mediaUrl,
      dominantColor: null,
      product: null,
      offer: null,
      stores: null,
      cities: null,
      screenPosition: cafeTV.screenPosition || "Screen 0",
    });

    // Fetch all menuItems by their document IDs.
    // For slideshow menu items, expand each mediaList entry into a separate banner.
    const bannerGroups = await Promise.all(
      cafeTVsData.map(async (cafeTV) => {
        try {
          const menuItemDoc = await firestore
            .collection('menuItems')
            .doc(cafeTV.menuItemId)
            .get();

          if (!menuItemDoc.exists) {
            console.log('MenuItem not found:', cafeTV.menuItemId);
            return null;
          }

          const menuItem = menuItemDoc.data();

          // Extract screen number from screenPosition (e.g., "Screen 2" -> 2)
          const screenPosition = cafeTV.screenPosition || "Screen 0";
          const screenNumber = parseInt(screenPosition.replace(/\D/g, ''), 10) || 0;

          const itemType = (menuItem.type || "").toLowerCase().trim();
          const mediaList = Array.isArray(menuItem.mediaList) ? menuItem.mediaList : [];

          if (itemType === "slideshow" && mediaList.length > 0) {
            const slideshowBanners = mediaList
              .map((media, index) => {
                const mediaUrl = media?.url || "";
                if (!mediaUrl) {
                  return null;
                }

                const mediaType = detectMediaType(mediaUrl, media?.type || "");
                return buildSingleBanner({
                  cafeTV,
                  menuItem,
                  screenNumber,
                  mediaUrl,
                  mediaType,
                  duration: media?.duration || menuItem.duration || 5,
                  idSuffix: `-${index}`,
                });
              })
              .filter(Boolean);

            return slideshowBanners;
          }

          const mediaUrl = menuItem.imageUrl || "";
          const mediaType = detectMediaType(mediaUrl, menuItem.type || "");
          if (!mediaUrl) {
            return [];
          }

          return [
            buildSingleBanner({
              cafeTV,
              menuItem,
              screenNumber,
              mediaUrl,
              mediaType,
              duration: menuItem.duration || 5,
            }),
          ];
        } catch (err) {
          console.error('Error fetching menuItem:', cafeTV.menuItemId, err);
          return [];
        }
      })
    );

    const banners = bannerGroups.flat();

    // Filter out null entries and sort by screenPosition
    const validBanners = banners
      .filter((banner) => banner && banner.mediaUrl)
      .sort((a, b) => a.preference - b.preference);
    console.log('Firebase banners loaded:', validBanners.length);

    return validBanners;
  } catch (error) {
    console.error("Error fetching banners from Firebase:", error);
    return [];
  }
};
